import { Link } from "react-router-dom";
import type { Client, KillSwitchState } from "@/api/schemas";
import { ClientBrand } from "@/components/clients/ClientBrand";
import { ClientPickerPopover } from "@/components/clients/ClientPickerPopover";
import { gruposDeRol, destinoPorId, rutaDeCliente, type DestinoId, type Rol } from "@/routes/routeConfig";
import type { NavBadge } from "@/utils/navBadges";
import { FreshnessBadge } from "./FreshnessBadge";
import { NavIcon } from "./NavIcon";
import { UserMenu } from "./UserMenu";
import styles from "./Sidebar.module.css";

/** Destinos de agencia que no pertenecen a ningún cliente. */
export type DestinoDeAgencia = "clientes" | "agencia-equipo";

export interface SidebarProps {
  rol: Rol;
  email: string;
  /** Cliente que da contexto al marco (en la tienda, el suyo aunque la URL no lo nombre). */
  cliente: Client | null;
  clients: readonly Client[];
  recientes: readonly string[];
  onSelectClient: (client: Client) => void;
  destinoActivo: DestinoId | null;
  destinoDeAgenciaActivo: DestinoDeAgencia | null;
  badges: Partial<Record<DestinoId, NavBadge>>;
  lagMinutes: number | undefined;
  noData?: boolean;
  killSwitch: KillSwitchState | undefined;
  onOpenBrake: () => void;
  onOpenHelp: () => void;
  onLogout: () => void;
  /** Solo iconos (64 px). */
  compacta: boolean;
  /** 600–1199 px y desplegada encima del contenido. */
  sobrepuesta: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}

function Counter({ badge }: { badge: NavBadge | undefined }) {
  if (!badge) return null;
  return (
    <>
      {badge.count ? (
        <span className={styles.count}>
          {badge.count}
          <span className="visually-hidden"> {badge.countText}</span>
        </span>
      ) : null}
      {badge.dot ? <span className={`${styles.dot} ${badge.dot === "red" ? styles.dotRed : styles.dotAmber}`} role="img" aria-label={badge.dotText} /> : null}
    </>
  );
}

/**
 * Barra lateral (visual.md §3): selector de cliente arriba (solo agencia), navegación por grupos y pie
 * con Clientes, «Parar cambios», ayuda y menú de la persona. 240 px, o 64 px solo con iconos.
 * La tienda lleva únicamente sus 5 secciones: sin grupos, sin freno y sin ayuda visible.
 */
export function Sidebar(props: SidebarProps) {
  const { rol, cliente, destinoActivo, destinoDeAgenciaActivo, badges, killSwitch, compacta, sobrepuesta } = props;
  const engaged = killSwitch?.effective.engaged ?? false;
  const esAgencia = rol === "agency";
  const grupos = gruposDeRol(rol);

  function linkDe(id: DestinoId) {
    const destino = destinoPorId(id);
    const activo = destinoActivo === id;
    return (
      <Link
        key={id}
        to={cliente ? rutaDeCliente(cliente.slug, destino.ruta) : "/"}
        className={`${styles.item} ${activo ? styles.itemActive : ""}`}
        aria-current={activo ? "page" : undefined}
        data-etiqueta={destino.label}
        onClick={props.onNavigate}
      >
        <NavIcon name={id} />
        <span className={styles.label}>{destino.label}</span>
        <Counter badge={badges[id]} />
      </Link>
    );
  }

  return (
    <aside className={styles.sidebar} data-compacta={compacta} data-sobrepuesta={sobrepuesta} data-rol={rol}>
      <div className={styles.selector}>
        {esAgencia ? (
          <ClientPickerPopover
            variant="lateral"
            clients={props.clients}
            actual={cliente}
            recientes={props.recientes}
            onSelect={props.onSelectClient}
          />
        ) : cliente ? (
          <div className={styles.marca}>
            <ClientBrand client={cliente} size={32} />
            <span className={styles.marcaNombre} title={cliente.name}>{cliente.name}</span>
          </div>
        ) : null}
      </div>

      {cliente ? (
        <nav className={styles.nav} aria-label={`Secciones de ${cliente.name}`}>
          <div className={styles.grupo}>{linkDe("inicio")}</div>
          {grupos.map((grupo) => (
            <div className={styles.grupo} key={grupo.titulo ?? "sin-titulo"}>
              {grupo.titulo ? <p className={styles.grupoTitulo}>{grupo.titulo}</p> : null}
              {grupo.destinos.map((destino) => linkDe(destino.id))}
            </div>
          ))}
        </nav>
      ) : (
        <div className={styles.nav} />
      )}

      <div className={styles.pie}>
        {esAgencia ? (
          <>
            <span className={styles.frescura}>
              <FreshnessBadge lagMinutes={props.lagMinutes} noData={props.noData} />
            </span>
            <nav aria-label="Agencia" className={styles.grupo}>
              <Link to="/clientes" className={`${styles.item} ${destinoDeAgenciaActivo === "clientes" ? styles.itemActive : ""}`} aria-current={destinoDeAgenciaActivo === "clientes" ? "page" : undefined} data-etiqueta="Clientes" onClick={props.onNavigate}>
                <NavIcon name="clientes" />
                <span className={styles.label}>Clientes</span>
              </Link>
              <Link to="/agencia/equipo" className={`${styles.item} ${destinoDeAgenciaActivo === "agencia-equipo" ? styles.itemActive : ""}`} aria-current={destinoDeAgenciaActivo === "agencia-equipo" ? "page" : undefined} data-etiqueta="Equipo de la agencia" onClick={props.onNavigate}>
                <NavIcon name="agencia-equipo" />
                <span className={styles.label}>Equipo de la agencia</span>
              </Link>
            </nav>
            <button
              type="button"
              className={`${styles.freno} ${engaged ? styles.frenoActivo : ""}`}
              onClick={props.onOpenBrake}
              aria-pressed={killSwitch ? engaged : undefined}
              aria-label={!killSwitch ? "Parar cambios: estado sin verificar" : undefined}
              data-etiqueta={engaged ? "Cambios parados" : "Parar cambios"}
            >
              <span className={styles.frenoIcono} aria-hidden="true" />
              <span className={styles.label}>{engaged ? "Cambios parados" : "Parar cambios"}</span>
            </button>
            <button type="button" className={styles.ayuda} onClick={props.onOpenHelp} aria-label="Atajos de teclado" data-etiqueta="Atajos de teclado">
              <span className={styles.ayudaIcono} aria-hidden="true">?</span>
              <span className={styles.label}>Atajos</span>
            </button>
          </>
        ) : null}
        <UserMenu email={props.email} onLogout={props.onLogout} variante="pie" />
        <button type="button" className={styles.plegar} onClick={props.onToggle} aria-expanded={!compacta} aria-label={compacta ? "Desplegar la barra lateral" : "Plegar la barra lateral"} data-etiqueta={compacta ? "Desplegar" : "Plegar"}>
          <NavIcon name="plegar" />
        </button>
      </div>
    </aside>
  );
}
